// The SS Atlantica, built procedurally: an extruded hull from the board's deck outline,
// planked deck, open-topped ("cut-away") interior rooms, funnel, masts and rails.
// Every game space is a clickable flat region; water spaces sit on the sea around the hull.
import { Html, Line } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef, useState } from "react";
import * as THREE from "three";

import * as uf from "../../../../../common/unfathomable";
import { kSpaceGeom } from "../boardGeometry";
import { kDeckY, kHullOutline, isWater, shapeFromXZ, spaceCentre, spacePolyXZ, spaceShape, toXZ } from "./coords";
import css from "../Unfathomable.module.css";
import { Fire, Plume } from "./fx";

const kWallH = 0.42;

function plankTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 512; c.height = 512;
  const g = c.getContext("2d")!;
  for (let i = 0; i < 32; i++) {
    const shade = 120 + ((i * 37) % 30);
    g.fillStyle = `rgb(${shade},${Math.round(shade * 0.72)},${Math.round(shade * 0.45)})`;
    g.fillRect(0, i * 16, 512, 16);
    g.fillStyle = "rgba(40,24,10,0.55)";
    g.fillRect(0, i * 16 + 15, 512, 1);
    for (let k = 0; k < 3; k++) { g.fillRect(((i * 131 + k * 173) % 512), i * 16, 1, 16); }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(0.12, 0.12);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Flat region at height y for a space (used for floors, highlights and hit testing). */
function useSpaceGeometry(sp: uf.SpaceId) {
  return useMemo(() => new THREE.ShapeGeometry(spaceShape(sp)), [sp]);
}

function Hull() {
  const [upper, lower] = useMemo(() => {
    const shape = shapeFromXZ(kHullOutline);
    const up = new THREE.ExtrudeGeometry(shape, { depth: 1.25, bevelEnabled: true, bevelThickness: 0.08, bevelSize: 0.08, bevelSegments: 2 });
    const low = new THREE.ExtrudeGeometry(shapeFromXZ(kHullOutline.map(([x, z]) => [x * 0.985, z * 0.94])), { depth: 1.2, bevelEnabled: true, bevelThickness: 0.2, bevelSize: 0.2, bevelSegments: 3 });
    return [up, low];
  }, []);
  const deckGeo = useMemo(() => new THREE.ShapeGeometry(shapeFromXZ(kHullOutline)), []);
  const planks = useMemo(plankTexture, []);
  // Bulwark: short walls along the deck edge.
  const rails = useMemo(() => {
    const out: { pos: [number, number, number]; len: number; rot: number }[] = [];
    for (let i = 0; i < kHullOutline.length; i++) {
      const [x1, z1] = kHullOutline[i], [x2, z2] = kHullOutline[(i + 1) % kHullOutline.length];
      const len = Math.hypot(x2 - x1, z2 - z1);
      out.push({ pos: [(x1 + x2) / 2, kDeckY + 0.18, (z1 + z2) / 2], len, rot: -Math.atan2(z2 - z1, x2 - x1) });
    }
    return out;
  }, []);
  return (
    <group>
      {/* Extrusions grow by their bevel on both ends: keep the top just under the deck planks. */}
      <mesh geometry={upper} rotation={[-Math.PI / 2, 0, 0]} position={[0, kDeckY - 1.25 - 0.1, 0]} castShadow receiveShadow>
        <meshStandardMaterial color="#1c1a1a" roughness={0.6} metalness={0.4} />
      </mesh>
      <mesh geometry={lower} rotation={[-Math.PI / 2, 0, 0]} position={[0, kDeckY - 2.45, 0]}>
        <meshStandardMaterial color="#6b1f1a" roughness={0.7} metalness={0.2} />
      </mesh>
      <mesh geometry={deckGeo} rotation={[-Math.PI / 2, 0, 0]} position={[0, kDeckY + 0.002, 0]} receiveShadow>
        <meshStandardMaterial map={planks} roughness={0.85} />
      </mesh>
      {rails.map((r, i) => (
        <mesh key={i} position={r.pos} rotation={[0, r.rot, 0]} castShadow>
          <boxGeometry args={[r.len + 0.06, 0.36, 0.08]} />
          <meshStandardMaterial color="#e9e1cf" roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/** Walls for every interior room (shared edges deduplicated), with a brass cap. */
function RoomWalls({ damaged }: { damaged: Set<string> }) {
  const walls = useMemo(() => {
    const seen = new Set<string>();
    const out: { pos: [number, number, number]; len: number; rot: number; rooms: string[] }[] = [];
    const key = (a: [number, number], b: [number, number]) => {
      const k1 = `${a[0].toFixed(2)},${a[1].toFixed(2)}`, k2 = `${b[0].toFixed(2)},${b[1].toFixed(2)}`;
      return k1 < k2 ? `${k1}|${k2}` : `${k2}|${k1}`;
    };
    const owner = new Map<string, number>();
    for (const sp of uf.kInteriorSpaces) {
      const poly = spacePolyXZ(sp);
      for (let i = 0; i < poly.length; i++) {
        const a = poly[i], b = poly[(i + 1) % poly.length];
        const k = key(a, b);
        if (seen.has(k)) { out[owner.get(k)!].rooms.push(sp); continue; }
        seen.add(k);
        const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (len < 0.05) continue;
        owner.set(k, out.length);
        out.push({ pos: [(a[0] + b[0]) / 2, kDeckY + kWallH / 2, (a[1] + b[1]) / 2], len, rot: -Math.atan2(b[1] - a[1], b[0] - a[0]), rooms: [sp] });
      }
    }
    return out;
  }, []);
  return (
    <group>
      {walls.map((w, i) => {
        const broken = w.rooms.some(r => damaged.has(r));
        return (
          <group key={i} position={w.pos} rotation={[0, w.rot, 0]}>
            <mesh castShadow receiveShadow scale={[1, broken ? 0.55 : 1, 1]} position={[0, broken ? -kWallH * 0.22 : 0, 0]}>
              <boxGeometry args={[w.len, kWallH, 0.09]} />
              <meshStandardMaterial color={broken ? "#3b2a22" : "#8a6040"} roughness={0.75} />
            </mesh>
            {!broken && (
              <mesh position={[0, kWallH / 2 + 0.02, 0]}>
                <boxGeometry args={[w.len, 0.04, 0.13]} />
                <meshStandardMaterial color="#b8925a" metalness={0.7} roughness={0.35} />
              </mesh>
            )}
          </group>
        );
      })}
    </group>
  );
}

const kFloor: Partial<Record<uf.SpaceId, string>> = {
  bridge: "#3f4b52", chapel: "#4a3557", cabin: "#5a3a2a", cargo: "#4f4433", galley: "#5a5a4c", boiler: "#3a2e2a", sickbay: "#d9d6cc", brig: "#2a2a2c",
};

function SpaceRegion({ sp, label, target, damaged, onSpace }: {
  sp: uf.SpaceId; label: string; target: string | undefined; damaged: boolean; onSpace: (sp: uf.SpaceId) => void;
}) {
  const geo = useSpaceGeometry(sp);
  const water = isWater(sp);
  const kind = uf.spaceKind(sp);
  const [hover, setHover] = useState(false);
  const mat = useRef<THREE.MeshBasicMaterial>(null);
  useFrame(({ clock }) => {
    if (!mat.current) return;
    const pulse = 0.5 + 0.5 * Math.sin(clock.elapsedTime * 4);
    mat.current.opacity = target ? 0.28 + 0.3 * pulse + (hover ? 0.2 : 0) : hover ? 0.12 : 0;
  });
  const y = water ? 0.45 : kDeckY + 0.03;
  const outline = useMemo(() => {
    const p = spacePolyXZ(sp).map(([x, z]) => new THREE.Vector3(x, y + 0.01, z));
    return [...p, p[0]];
  }, [sp, y]);
  const [cx, cz] = spaceCentre(sp);
  return (
    <group>
      {kind === "interior" && (
        <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, kDeckY + 0.01, 0]} receiveShadow>
          <meshStandardMaterial color={damaged ? "#2a1210" : kFloor[sp] ?? "#444"} roughness={0.9} />
        </mesh>
      )}
      <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]}
        onPointerOver={e => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = "default"; }}
        onClick={e => { e.stopPropagation(); onSpace(sp); }}>
        <meshBasicMaterial ref={mat} color={target ? "#ffd56a" : "#bfe9d7"} transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <Line points={outline} color={target ? "#ffd56a" : water ? "#7fc7c0" : "#c9a86a"} lineWidth={target ? 2.4 : 1} transparent
        opacity={target ? 0.95 : water ? 0.22 : 0.5} dashed={water} dashSize={0.4} gapSize={0.3} />
      {hover && (
        <Html position={[cx, y + 0.9, cz]} center distanceFactor={14} zIndexRange={[5, 0]} style={{ pointerEvents: "none" }}>
          <div className={css.spaceLabel} data-target={target ? "1" : "0"}>{label}{target ? ` · ${target}` : ""}</div>
        </Html>
      )}
    </group>
  );
}

function Funnel() {
  const [x, z] = toXZ(1720, 915);
  return (
    <group position={[x, kDeckY, z]}>
      <mesh position={[0, 2.2, 0]} rotation={[0, 0, 0.12]} castShadow>
        <cylinderGeometry args={[0.55, 0.62, 4.4, 24]} />
        <meshStandardMaterial color="#d9a441" roughness={0.5} metalness={0.3} />
      </mesh>
      <mesh position={[0.27, 4.25, 0]} rotation={[0, 0, 0.12]}>
        <cylinderGeometry args={[0.57, 0.57, 0.6, 24]} />
        <meshStandardMaterial color="#141414" roughness={0.6} />
      </mesh>
      <Plume position={[0.35, 4.6, 0]} count={22} color="#3c3a38" spread={0.4} height={7} size={1.6} speed={0.9} opacity={0.5} seed={11} />
    </group>
  );
}

function Mast({ bx, h }: { bx: number; h: number }) {
  const [x, z] = toXZ(bx, 915);
  return (
    <group position={[x, kDeckY, z]}>
      <mesh position={[0, h / 2, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.12, h, 10]} />
        <meshStandardMaterial color="#4a3320" roughness={0.8} />
      </mesh>
      <mesh position={[0, h * 0.72, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 3.2, 8]} />
        <meshStandardMaterial color="#4a3320" roughness={0.8} />
      </mesh>
      <mesh position={[0, h + 0.1, 0]}>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color="#ffd27a" emissive="#ffb347" emissiveIntensity={2} />
      </mesh>
      <pointLight position={[0, h + 0.1, 0]} color="#ffb347" intensity={4} distance={7} />
    </group>
  );
}

/** Red cross for the sick bay, bars for the brig. */
function RoomProps() {
  const [sx, sz] = spaceCentre("sickbay");
  const brig = kSpaceGeom.brig.poly.map(([x, y]) => toXZ(x, y));
  const [bx1, bz1] = brig[0], [bx2] = brig[1];
  return (
    <group>
      <group position={[sx, kDeckY + 0.03, sz]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh><planeGeometry args={[0.9, 0.28]} /><meshBasicMaterial color="#b01e1e" /></mesh>
        <mesh><planeGeometry args={[0.28, 0.9]} /><meshBasicMaterial color="#b01e1e" /></mesh>
      </group>
      {Array.from({ length: 9 }).map((_, i) => (
        <mesh key={i} position={[bx1 + ((bx2 - bx1) * (i + 0.5)) / 9, kDeckY + kWallH / 2 + 0.05, bz1]}>
          <cylinderGeometry args={[0.025, 0.025, kWallH + 0.1, 6]} />
          <meshStandardMaterial color="#8b8d90" metalness={0.9} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

/** Room floors only (no hover areas, outlines or labels): what the shipwreck halves carry. */
function Floor({ sp, damaged }: { sp: uf.SpaceId; damaged: boolean }) {
  const geo = useSpaceGeometry(sp);
  if (uf.spaceKind(sp) !== "interior") return null;
  return (
    <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, kDeckY + 0.01, 0]} receiveShadow>
      <meshStandardMaterial color={damaged ? "#2a1210" : kFloor[sp] ?? "#444"} roughness={0.9} />
    </mesh>
  );
}

/** Hull, rooms and fittings. Without `onSpace` it is inert (the shipwreck renders it twice). */
export function ShipBody({ view, targets, onSpace }: {
  view: uf.GameView;
  targets?: Map<uf.SpaceId, string>;
  onSpace?: (sp: uf.SpaceId) => void;
}) {
  const damaged = useMemo(() => new Set(uf.kInteriorSpaces.filter(sp => view.spaces[sp].damage)), [view.spaces]);
  const shipSpaces = (Object.keys(kSpaceGeom) as uf.SpaceId[]).filter(sp => !isWater(sp));
  return (
    <>
      <Hull />
      <RoomWalls damaged={damaged} />
      <RoomProps />
      <Funnel />
      <Mast bx={200} h={6.5} />
      <Mast bx={1880} h={5.5} />
      {shipSpaces.map(sp => onSpace
        ? <SpaceRegion key={sp} sp={sp} label={uf.spaceName(sp)} target={targets?.get(sp)} damaged={damaged.has(sp as uf.InteriorSpaceId)} onSpace={onSpace} />
        : <Floor key={sp} sp={sp} damaged={damaged.has(sp as uf.InteriorSpaceId)} />)}
      {[...damaged].map((sp, i) => {
        const [x, z] = spaceCentre(sp as uf.SpaceId);
        return <Fire key={sp} position={[x, kDeckY + 0.05, z]} seed={i * 13 + 5} />;
      })}
    </>
  );
}

/**
 * The ship, gently rolling, pitching and heaving with the sea (more in a storm). `children` (the
 * pieces on deck) ride along. The rocking eases toward the weather instead of jumping.
 */
export function Ship({ view, targets, onSpace, storm, rockRef, children }: {
  view: uf.GameView;
  targets: Map<uf.SpaceId, string>;
  onSpace: (sp: uf.SpaceId) => void;
  storm: number;
  /** Receives the rocking group (pieces outside the ship use it to ride the deck). */
  rockRef?: React.MutableRefObject<THREE.Group | null>;
  children?: React.ReactNode;
}) {
  const own = useRef<THREE.Group>(null);
  const group = rockRef ?? own;
  const sea = useRef(storm);
  useFrame(({ clock }, dt) => {
    const g = group.current;
    if (!g) return;
    sea.current += (storm - sea.current) * Math.min(1, dt * 0.5);
    const t = clock.elapsedTime, a = sea.current;
    // Two incommensurate swells per axis so the motion never looks like a metronome.
    g.rotation.x = (Math.sin(t * 0.62) * 0.7 + Math.sin(t * 1.13 + 2) * 0.3) * (0.014 + 0.03 * a); // roll
    g.rotation.z = (Math.sin(t * 0.41 + 1) * 0.7 + Math.sin(t * 0.87) * 0.3) * (0.006 + 0.014 * a); // pitch
    g.position.y = (Math.sin(t * 0.9) * 0.7 + Math.sin(t * 1.7 + 1) * 0.3) * (0.05 + 0.1 * a); // heave
  });
  const waterSpaces = (Object.keys(kSpaceGeom) as uf.SpaceId[]).filter(isWater);
  return (
    <>
      <group ref={group}>
        <ShipBody view={view} targets={targets} onSpace={onSpace} />
        {children}
      </group>
      {waterSpaces.map(sp => (
        <SpaceRegion key={sp} sp={sp} label={uf.spaceName(sp)} target={targets.get(sp)} damaged={false} onSpace={onSpace} />
      ))}
    </>
  );
}
